import { ArrowPathIcon } from "@heroicons/react/24/outline";
import { ClientId, daysAgo, FeedbackType } from "@sp-crm/core";
import { useMutation, useQuery } from "@tanstack/react-query";
import { InputFormBlock } from "components/app/input-form-block";
import {
    InlineFeedback,
    InlineFeedbackResponse,
} from "components/feedback/inline-feedback";
import { Header } from "components/header";
import { trpc } from "components/rpc/trpc";
import { AutosavingInput } from "components/ui/autosaving-input";
import { SecondaryButton } from "components/ui/secondary-button";
import { Spinner } from "components/ui/spinner";
import React, { useCallback, useState } from "react";
import { handleEvent } from "util/user-events";

interface AiClientSummaryProps {
    clientId: ClientId;
}

const FEEDBACK_OPTIONS = [
    "Inaccurate information",
    "Missing important details",
    "Too long",
    "Too short",
    "Not relevant",
];

export const AiSummary: React.FC<AiClientSummaryProps> = ({ clientId }) => {
    const trpcClient = trpc.useTRPC();
    const [feedbackGiven, setFeedbackGiven] = useState<InlineFeedbackResponse>(null);
    const [showRemoveButton, setShowRemoveButton] = useState(false);

    const {
        data: summary,
        isLoading: isQueryLoading,
        error: queryError,
        refetch: refetchSummary,
    } = useQuery({
        ...trpcClient.clientAiSummary.find.queryOptions({ clientId }),
    });

    const generateSummaryMutation = useMutation({
        ...trpcClient.clientAiSummary.generate.mutationOptions(),
        onSuccess: () => {
            setFeedbackGiven(null);
            setShowRemoveButton(false);
            refetchSummary();
        },
    });

    const submitFeedbackMutation = useMutation({
        ...trpcClient.clientAiSummary.submitFeedback.mutationOptions(),
    });

    const updateTextMutation = useMutation({
        ...trpcClient.clientAiSummary.updateText.mutationOptions(),
        onSuccess: () => {
            refetchSummary();
        },
    });

    const removeSummaryMutation = useMutation({
        ...trpcClient.clientAiSummary.removeSummary.mutationOptions(),
        onSuccess: () => {
            setFeedbackGiven(null);
            setShowRemoveButton(false);
            refetchSummary();
        },
    });

    const handleUpdateText = useCallback(
        (newText: string) => {
            if (!summary) return;
            updateTextMutation.mutate({
                summaryId: summary.id,
                text: newText,
            });
        },
        [summary, updateTextMutation],
    );

    const handleGenerateOrRegenerateSummary = useCallback(async () => {
        handleEvent("ai-summary-generate", {
            clientId,
            isRegenerate: !!summary,
        });
        generateSummaryMutation.mutate({
            clientId,
        });
    }, [clientId, generateSummaryMutation, summary]);

    const handlePositive = useCallback(() => {
        if (!summary) return;
        setFeedbackGiven("positive");
        submitFeedbackMutation.mutate({
            summaryId: summary.id,
            feedbackType: FeedbackType.Positive,
        });
    }, [summary, submitFeedbackMutation]);

    const handleNegativeIntent = useCallback(() => {
        if (!summary) return;
        setFeedbackGiven("negative");
        setShowRemoveButton(true);
    }, [summary]);

    const handleNegativeSubmit = useCallback(
        (combinedText: string | null) => {
            if (!summary) return;
            submitFeedbackMutation.mutate({
                summaryId: summary.id,
                feedbackType: FeedbackType.Negative,
                feedbackText: combinedText || undefined,
            });
        },
        [summary, submitFeedbackMutation],
    );

    const handleRemoveSummary = useCallback(() => {
        if (!summary) return;
        removeSummaryMutation.mutate({
            summaryId: summary.id,
        });
    }, [summary, removeSummaryMutation]);

    const isLoading = isQueryLoading || generateSummaryMutation.isPending;
    const error =
        queryError?.message ||
        generateSummaryMutation.error?.message ||
        updateTextMutation.error?.message;

    return (
        <InputFormBlock>
            <Header
                iconName="29_ClientSummary"
                action={
                    <SecondaryButton
                        disabled={isLoading}
                        onClick={handleGenerateOrRegenerateSummary}
                        className="flex items-center space-x-2">
                        {isLoading ? <Spinner /> : <ArrowPathIcon className="w-4 h-4" />}
                        {summary ? (
                            <span className="text-sm">Regenerate Summary</span>
                        ) : (
                            <span className="text-sm">Generate Summary</span>
                        )}
                    </SecondaryButton>
                }>
                AI Summary
            </Header>

            {error && (
                <div className="text-red-600 text-sm mb-4 p-3 bg-red-50 rounded-md">
                    {error}
                </div>
            )}

            {summary ? (
                <div className="space-y-2">
                    <AutosavingInput
                        initial={summary.text}
                        onCommit={handleUpdateText}
                        multiLine
                        autoGrow
                        rows={4}
                    />

                    <div className="flex items-start justify-between">
                        <div>
                            <p className="text-sm text-gray-500">
                                Generated {daysAgo(summary.dateAdded)}
                            </p>
                            {summary.editedAt ? (
                                <p className="text-sm text-gray-500">
                                    Edited by {summary.editedByName} on{" "}
                                    {daysAgo(summary.editedAt)}
                                </p>
                            ) : null}
                        </div>
                        <div className="flex flex-col items-end gap-2">
                            <InlineFeedback
                                negativeFeedbackPrompt="What could be improved?"
                                negativeFeedbackOptions={FEEDBACK_OPTIONS}
                                submitted={feedbackGiven}
                                submitting={submitFeedbackMutation.isPending}
                                positiveAriaLabel="This summary is helpful"
                                negativeAriaLabel="This summary needs improvement"
                                onPositive={handlePositive}
                                onNegativeIntent={handleNegativeIntent}
                                onNegativeSubmit={handleNegativeSubmit}
                            />
                            {showRemoveButton && (
                                <button
                                    type="button"
                                    onClick={handleRemoveSummary}
                                    disabled={removeSummaryMutation.isPending}
                                    className="text-sm text-red-600 hover:text-red-700 font-medium disabled:opacity-50">
                                    {removeSummaryMutation.isPending
                                        ? "Removing..."
                                        : "Remove Summary"}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            ) : (
                <p className="text-gray-600 text-sm italic">
                    Generate an AI-powered summary of this client&apos;s information,
                    notes, and communications.
                </p>
            )}
        </InputFormBlock>
    );
};
